import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { createContext, useEffect, useState, useContext } from "react";
import { auth } from "../../firebase-config";
import { storeFCMToken } from "../../repository/UserDAO";
import { UserStatus } from "../../components/routes/routing_statuses/RoutingStatuses";

const AuthContext = createContext();

let pendingFCMToken = null;
let isInitialized = false;

window.receiveFCMToken = function(token) {
  if (!token) {
    return;
  }

  if (!isInitialized) {
    pendingFCMToken = token;
  } else if (auth.currentUser) {
    storeFCMToken(auth.currentUser.uid, token);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(UserStatus.UNINITIALIZED);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        isInitialized = true;
        // If there's a pending FCM token, store it now that user is authenticated
        if (pendingFCMToken) {
          storeFCMToken(currentUser.uid, pendingFCMToken);
        }
      } else {
        setUser(UserStatus.UNAUTHENTICATED);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const createUser = async (email, password) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      return error.code;
    }
  };

  const loginUser = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { user: userCredential.user };
    } catch (error) {
      return { error: error.message };
    }
  };

  const resetEmail = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: "Password reset email sent successfully." };
    } catch (error) {
      return { error: error.message };
    }
  };

  const logOut = async () => {
    try {
      await signOut(auth);
      // Ensure user state is explicitly set to UNAUTHENTICATED
      setUser(UserStatus.UNAUTHENTICATED);
    } catch (error) {
      // Handle error silently
    }
  };

  return (
    <AuthContext.Provider value={{ 
      createUser,
      user,
      loginUser,
      logOut,
      resetEmail,
     }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthContext = () => {
  return useContext(AuthContext);
};